import { useCallback } from 'react';

import { useCurrentOrganization, useSelectedTenant } from './useSwr';

export type PermissionsEnum =
  | 'annotation-queues:create'
  | 'annotation-queues:delete'
  | 'annotation-queues:read'
  | 'annotation-queues:update'
  | 'datasets:create'
  | 'datasets:delete'
  | 'datasets:read'
  | 'datasets:share'
  | 'datasets:update'
  | 'deployments:create'
  | 'deployments:delete'
  | 'deployments:read'
  | 'deployments:update'
  | 'feedback:create'
  | 'feedback:delete'
  | 'feedback:read'
  | 'feedback:update'
  | 'projects:create'
  | 'projects:delete'
  | 'projects:read'
  | 'projects:update'
  | 'prompts:create'
  | 'prompts:delete'
  | 'prompts:read'
  | 'prompts:update'
  | 'prompts:share'
  | 'runs:create'
  | 'runs:read'
  | 'runs:share'
  | 'rules:create'
  | 'rules:delete'
  | 'rules:read'
  | 'rules:update'
  | 'workspaces:manage'
  | 'workspaces:read'
  | 'organization:read'
  | 'organization:manage'
  | 'charts:create'
  | 'charts:delete'
  | 'charts:read'
  | 'charts:update';

// Organization-specific permissions
export const ORGANIZATION_PERMISSIONS = [
  'organization:read',
  'organization:manage',
] as const;

// Type for workspace-only permissions
export type WorkspacePermissions = Exclude<
  PermissionsEnum,
  (typeof ORGANIZATION_PERMISSIONS)[number]
>;

export type PermissionScope = 'workspace' | 'organization' | undefined;

// Type guard to check if a permission is organization-specific
function isOrganizationPermission(
  permission: PermissionsEnum
): permission is (typeof ORGANIZATION_PERMISSIONS)[number] {
  return ORGANIZATION_PERMISSIONS.includes(permission as any);
}

export function usePermissions(scope?: PermissionScope) {
  const selectedTenant = useSelectedTenant(scope === 'organization');
  const organization = useCurrentOrganization({
    skip: scope !== 'organization',
  });
  const permissionScope =
    scope === 'organization' ? organization : selectedTenant;

  const authorize = useCallback(
    (permission: PermissionsEnum) => {
      // When in workspace scope, reject organization-specific permissions
      if (scope !== 'organization' && isOrganizationPermission(permission)) {
        return false;
      }

      return permissionScope.data?.permissions?.includes(permission) ?? false;
    },
    [permissionScope.data?.permissions, scope]
  );

  const hasHomepageAccess = (
    [
      'annotation-queues:read',
      'datasets:read',
      'projects:read',
      'prompts:read',
      'deployments:read',
      'charts:read',
    ] as PermissionsEnum[]
  ).some((p) => authorize(p));

  return {
    authorize,
    hasHomepageAccess,
    isLoading: selectedTenant.isLoading || organization.isLoading,
  };
}
